import { useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

const SIZES = new Set(['small', 'medium', 'tall', 'fullscreen']);

// Open footers, by modal. The toast stack (ToastStack) sits above the highest
// one through --toast-lift so toasts never cover a modal's buttons.
const footerLifts = new Map();

function applyToastLift() {
    const lift = Math.max(0, ...footerLifts.values());
    document.documentElement.style.setProperty('--toast-lift', `${lift}px`);
}

function normalizeCloseOnBackdrop(value) {
    if (value === false) return false;
    if (value === 'click' || value === 'mousedown') return value;
    if (value === true) return 'mousedown';
    return 'mousedown';
}

/**
 * Universal dialog shell — portal, blur backdrop, size variants, header/footer slots.
 */
export default function OverlayModal({
    isOpen,
    onClose,
    title = '',
    description = '',
    children,
    footer = null,
    headerActions = null,
    size = 'medium',
    closeOnBackdrop = 'mousedown',
    closeOnEscape = true,
    hideHeader = false,
    hideClose = false,
    labelledBy,
    describedBy,
    zIndex = 200,
    maxWidthClass = '',
    overlayClassName = '',
    contentClassName = '',
    bodyClassName = '',
    footerClassName = '',
    panelProps = {},
}) {
    const autoTitleId = useId();
    const autoDescId = useId();
    const titleId = labelledBy || (title ? autoTitleId : undefined);
    const descId = describedBy || (description ? autoDescId : undefined);
    const resolvedSize = SIZES.has(size) ? size : 'medium';
    const backdropMode = normalizeCloseOnBackdrop(closeOnBackdrop);
    const footerRef = useRef(null);
    const hasFooter = Boolean(footer);

    useEffect(() => {
        const node = footerRef.current;
        if (!isOpen || !hasFooter || !node || typeof window === 'undefined') return undefined;
        const key = {};
        const measure = () => {
            const top = node.getBoundingClientRect().top;
            footerLifts.set(key, Math.max(0, Math.round(window.innerHeight - top)));
            applyToastLift();
        };
        measure();
        const settle = window.setTimeout(measure, 350);
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(node);
        observer?.observe(document.documentElement);
        window.addEventListener('resize', measure);
        return () => {
            window.clearTimeout(settle);
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            footerLifts.delete(key);
            applyToastLift();
        };
    }, [isOpen, hasFooter]);

    useEffect(() => {
        if (!isOpen || typeof document === 'undefined') return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !closeOnEscape || typeof document === 'undefined') return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose?.();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, closeOnEscape, onClose]);

    if (!isOpen || typeof document === 'undefined') return null;

    const dismiss = (event) => {
        if (!backdropMode) return;
        if (event.target !== event.currentTarget) return;
        onClose?.(event);
    };

    const backdropHandlers = {};
    if (backdropMode === 'mousedown') {
        backdropHandlers.onMouseDown = dismiss;
    } else if (backdropMode === 'click') {
        backdropHandlers.onClick = dismiss;
    }

    const showHeader = !hideHeader && (title || description || !hideClose);
    const { className: panelClassNameExtra = '', onMouseDown: panelMouseDown, ...restPanelProps } = panelProps;

    return createPortal(
        <div
            className={`admin-modal admin-modal--${resolvedSize} ${overlayClassName}`.trim()}
            style={{ zIndex }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            aria-label={!titleId ? (title || 'Modal') : undefined}
            {...backdropHandlers}
        >
            <div
                className={[
                    'admin-modal__panel',
                    maxWidthClass || contentClassName ? 'admin-modal__panel--custom-width' : '',
                    maxWidthClass,
                    contentClassName,
                    panelClassNameExtra,
                ].filter(Boolean).join(' ')}
                onMouseDown={(event) => {
                    event.stopPropagation();
                    panelMouseDown?.(event);
                }}
                onClick={(event) => event.stopPropagation()}
                {...restPanelProps}
            >
                {showHeader && (
                    <div className="admin-modal__head">
                        <div className="admin-modal__head-text">
                            {title ? (
                                <h2 id={titleId} className="admin-modal__title">
                                    {title}
                                </h2>
                            ) : null}
                            {description ? (
                                <p id={descId} className="admin-modal__description">
                                    {description}
                                </p>
                            ) : null}
                        </div>
                        {headerActions ? <div className="admin-modal__head-actions">{headerActions}</div> : null}
                        {!hideClose && (
                            <button
                                type="button"
                                className="admin-modal__close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}
                <div className={['admin-modal__body', bodyClassName].filter(Boolean).join(' ')}>
                    {children}
                </div>
                {footer ? (
                    <div ref={footerRef} className={['admin-modal__footer', footerClassName].filter(Boolean).join(' ')}>
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>,
        document.body,
    );
}

OverlayModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    children: PropTypes.node,
    footer: PropTypes.node,
    /** Controls in the header, before the close button. */
    headerActions: PropTypes.node,
    size: PropTypes.oneOf(['small', 'medium', 'tall', 'fullscreen']),
    closeOnBackdrop: PropTypes.oneOfType([
        PropTypes.oneOf(['mousedown', 'click']),
        PropTypes.bool,
    ]),
    closeOnEscape: PropTypes.bool,
    hideHeader: PropTypes.bool,
    hideClose: PropTypes.bool,
    labelledBy: PropTypes.string,
    describedBy: PropTypes.string,
    zIndex: PropTypes.number,
    maxWidthClass: PropTypes.string,
    overlayClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    bodyClassName: PropTypes.string,
    footerClassName: PropTypes.string,
    panelProps: PropTypes.object,
};
